import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { useSiteContext } from '@contexts/MkSiteContext'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { API_POINTS_BENEFITS } from '@constants'
import { serverUrl } from '@utils/MkEnv'
import { getPointsBenefitsLayoutMode } from '@account/utils/pointsBenefitsPresentation'

export interface PointsBenefitsYearPoints {
  availablePoints?: string
  totalPoints?: string
  /** ISO `yyyy-MM-dd` from the points-benefits API `yearPoints` object */
  useYearBegin?: string
  useYearEnd?: string
}

export interface PointsBenefitsBreakdownDetail {
  title?: string
  volume?: string
  note?: string
}

export interface PointsBenefitsBreakdown {
  linkText?: string
  details?: PointsBenefitsBreakdownDetail[]
  total?: { title?: string; volume?: string }
  availableToBorrow?: { title?: string; volume?: string }
}

/** Optional flags when opening the points/benefits breakdown modal (Figma copy). */
export interface BreakdownModalOpenOptions {
  useCreditsTotalLabel?: boolean
  benefitTitleForTotalRowFallback?: string
}

/** True when the breakdown has rows or summary fields to show (not only linkText). */
export function hasRenderablePointsBenefitsBreakdown(breakdown: PointsBenefitsBreakdown): boolean {
  const hasDetails = Boolean(breakdown.details?.length)
  const hasSummary = Boolean(breakdown.total || breakdown.availableToBorrow)
  return hasDetails || hasSummary
}

export interface PointsBenefitsMembershipSummary {
  memberId?: string
  name?: {
    first?: string
    last?: string
  }
  availablePoints?: string
  totalPoints?: string
  availableToBorrow?: string
  breakdown?: PointsBenefitsBreakdown
  yearPoints?: Record<string, PointsBenefitsYearPoints>
}

export interface PointsBenefitsData {
  membershipSummary?: PointsBenefitsMembershipSummary
  benefitYears?: Array<{
    label: string
    value: string
  }>
  defaultBenefitYear: string
  featuredBenefits?: {
    data?: Record<string, any>
  }
  remainingBenefits?: {
    data?: Record<string, any>
  }
  payment?: { title: string; url: string }
  hero?: {
    image?: string
  }
}

export interface UsePointsBenefitsReturn {
  data: PointsBenefitsData | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  reset: () => void
  getYearSpecificAvailablePoints: (selectedYear: number | string) => string
  getCurrentYearAvailablePoints: () => string
}

const normalizePointsBenefitsData = (pointsData: PointsBenefitsData): PointsBenefitsData => {
  const membershipSummary = pointsData?.membershipSummary
  const layoutMode = getPointsBenefitsLayoutMode(pointsData)

  if (!membershipSummary) {
    return pointsData
  }

  const normalizedAvailableToBorrow =
    membershipSummary.availableToBorrow ?? membershipSummary.breakdown?.availableToBorrow?.volume

  let normalizedBreakdown: PointsBenefitsBreakdown | undefined

  if (membershipSummary.breakdown) {
    normalizedBreakdown = {
      ...membershipSummary.breakdown,
      linkText: membershipSummary.breakdown.linkText ?? 'View Breakdown',
    }
  } else if (layoutMode === 'unified') {
    normalizedBreakdown = { linkText: 'View Breakdown' }
  }

  return {
    ...pointsData,
    membershipSummary: {
      ...membershipSummary,
      availableToBorrow: normalizedAvailableToBorrow,
      breakdown: normalizedBreakdown,
    },
  }
}

export const usePointsBenefits = (): UsePointsBenefitsReturn => {
  const [data, setData] = useState<PointsBenefitsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track if we've already fetched data to prevent infinite loops
  const hasFetchedRef = useRef(false)
  const jwtTokenRef = useRef<string | undefined>()

  const authHeaders = useAuthHeaders()
  const { themeBrand: brand } = useSiteContext()
  const { isAuthorized } = useAuthentication()

  const fetchPointsBenefits = useCallback(async () => {
    if (!isAuthorized) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${serverUrl + API_POINTS_BENEFITS}?brand=${brand}`, {
        method: 'GET',
        headers: authHeaders,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch points benefits: ${response.status}`)
      }

      const pointsData = await response.json()
      setData(normalizePointsBenefitsData(pointsData))
    } catch (err) {
      console.error('[usePointsBenefits] Error fetching points benefits:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch points benefits')
    } finally {
      setLoading(false)
    }
  }, [isAuthorized, authHeaders, brand])

  const refresh = useCallback(async () => {
    hasFetchedRef.current = false
    await fetchPointsBenefits()
  }, [fetchPointsBenefits])

  const reset = useCallback(() => {
    hasFetchedRef.current = false
    jwtTokenRef.current = undefined
    setData(null)
    setError(null)
  }, [])

  // Calculate available points for a specific year
  const getYearSpecificAvailablePoints = useCallback(
    (selectedYear: number | string): string => {
      if (!data?.remainingBenefits?.data) {
        return data?.membershipSummary?.availablePoints || '0'
      }

      if (getPointsBenefitsLayoutMode(data) === 'unified') {
        return data?.membershipSummary?.availablePoints ?? '0'
      }

      const yearData = data.remainingBenefits.data[selectedYear]
      const accordion = yearData?.accordion

      if (Array.isArray(accordion)) {
        const allBenefits = accordion.reduce((acc: any[], item: any) => {
          if (Array.isArray(item?.benefits)) {
            return acc.concat(item.benefits)
          }
          return acc
        }, [])

        const match = allBenefits.find((benefit: any) => typeof benefit?.title === 'string' && benefit.title.toLowerCase().includes('club wyndham plus points'))

        if (match) {
          const pts = parseInt(match.availablePoints || '0', 10)
          return Number.isNaN(pts) ? '0' : pts.toString()
        }
      }

      const fallback = parseInt(data?.membershipSummary?.availablePoints || '0', 10)
      return Number.isNaN(fallback) ? '0' : fallback.toString()
    },
    [data],
  )

  // Get current year available points
  const getCurrentYearAvailablePoints = useCallback((): string => {
    const currentYear = new Date().getFullYear()
    return getYearSpecificAvailablePoints(currentYear)
  }, [getYearSpecificAvailablePoints])

  useEffect(() => {
    // Only fetch if user is authorized and we haven't fetched yet, or if the JWT token changed
    if (isAuthorized && (!hasFetchedRef.current || jwtTokenRef.current !== authHeaders?.['X-JWT-Token'])) {
      jwtTokenRef.current = authHeaders?.['X-JWT-Token']
      hasFetchedRef.current = true
      fetchPointsBenefits()
    } else if (!isAuthorized) {
      // Reset state when user logs out
      reset()
    }
  }, [isAuthorized, authHeaders?.['X-JWT-Token'], brand, fetchPointsBenefits, reset])

  return {
    data,
    loading,
    error,
    refresh,
    reset,
    getYearSpecificAvailablePoints,
    getCurrentYearAvailablePoints,
  }
}
